import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MapPin, Upload, Plus, PencilSimple, ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const emptyPharm = {
  name: '', branch_name: '', area: '', address: '',
  latitude: '', longitude: '',
  contact_person: '', phone: '',
  commission_override: '',
  bank_name: '', bank_account: ''
};

export const PharmacyMapPage = () => {
  const { t } = useLanguage();
  const [pharmacies, setPharmacies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPharm);
  const fileRefs = useRef({});

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/pharmacies');
      setPharmacies(res.data);
    } catch (e) { console.error(e); }
  };

  const openNew = () => { setEditing(null); setForm(emptyPharm); setShowModal(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name || '', branch_name: p.branch_name || '', area: p.area || '', address: p.address || '',
      latitude: p.latitude ?? '', longitude: p.longitude ?? '',
      contact_person: p.contact_person || '', phone: p.phone || '',
      commission_override: p.commission_override ?? '',
      bank_name: p.bank_name || '', bank_account: p.bank_account || ''
    });
    setShowModal(true);
  };

  const geocode = async () => {
    if (!form.address.trim()) return toast.error('Enter an address first');
    try {
      const res = await api.get(`/settings/geocode`, { params: { address: form.address } });
      if (res.data?.lat != null) {
        setForm({ ...form, latitude: res.data.lat, longitude: res.data.lng });
        toast.success('Geocoded');
      } else toast.error('No result from geocoder');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Geocoder unavailable — enter lat/lng manually');
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.area.trim() || !form.contact_person.trim() || !form.phone.trim()) {
      return toast.error('Name, address, area, contact person and phone are required');
    }
    const payload = {
      ...form,
      latitude: form.latitude === '' ? null : parseFloat(form.latitude),
      longitude: form.longitude === '' ? null : parseFloat(form.longitude),
      commission_override: form.commission_override === '' ? null : parseFloat(form.commission_override),
      branch_name: form.branch_name || null,
      bank_name: form.bank_name || null,
      bank_account: form.bank_account || null,
    };
    try {
      if (editing) {
        await api.put(`/pharmacies/${editing}`, payload);
        toast.success('Pharmacy updated');
      } else {
        await api.post('/pharmacies', payload);
        toast.success('Pharmacy created');
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const toggleStatus = async (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    if (!window.confirm(`Set "${p.name}" to ${next.toUpperCase()}?`)) return;
    try {
      await api.put(`/pharmacies/${p.id}`, { status: next });
      toast.success(`Pharmacy ${next}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleUpload = async (pharmacyId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/settings/upload-storefront/${pharmacyId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Storefront photo uploaded');
      fetchData();
    } catch { toast.error('Upload failed'); }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/where-to-buy`;
    navigator.clipboard.writeText(url).then(() => toast.success('Public link copied')).catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>{t('nav.pharmacyMap')}</h1>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="copy-public-link-btn" variant="outline" onClick={copyPublicLink}>
            <ClipboardText size={16} className="mr-2" /> Copy public link
          </Button>
          <a href="/where-to-buy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center">
            View Public Map →
          </a>
          <Button data-testid="new-pharmacy-btn" onClick={openNew} style={{ backgroundColor: '#1E3A8A' }}>
            <Plus size={16} className="mr-2" /> Add Pharmacy
          </Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3">Storefront</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Branch</th>
              <th className="text-left p-3">Area</th>
              <th className="text-left p-3">Address</th>
              <th className="text-left p-3">Location</th>
              <th className="text-left p-3">Contact</th>
              <th className="text-right p-3">Commission</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pharmacies.map((p) => (
              <tr key={p.id} data-testid={`pharm-row-${p.id}`} className="border-t border-slate-200">
                <td className="p-3">
                  {p.storefront_photo ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/pharmacies/storefront-image/${p.id}`} alt={p.name} className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs">No photo</div>
                  )}
                </td>
                <td className="p-3 font-medium">{p.name}</td>
                <td className="p-3">{p.branch_name || '-'}</td>
                <td className="p-3">{p.area}</td>
                <td className="p-3 text-xs text-slate-600 max-w-xs">{p.address}</td>
                <td className="p-3">
                  {p.latitude && p.longitude ? (
                    <a href={`https://www.google.com/maps?q=${p.latitude},${p.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                      <MapPin size={14} /> View
                    </a>
                  ) : <span className="text-slate-400 text-xs">Not set</span>}
                </td>
                <td className="p-3 text-xs">
                  <div>{p.contact_person}</div>
                  <div className="text-slate-500">{p.phone}</div>
                </td>
                <td className="p-3 text-right">{p.commission_override != null ? `${p.commission_override}%` : 'Default'}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {(p.status || 'active').toUpperCase()}
                  </span>
                </td>
                <td className="p-3">
                  <input
                    type="file" accept="image/*"
                    ref={(el) => (fileRefs.current[p.id] = el)}
                    className="hidden"
                    onChange={(e) => handleUpload(p.id, e.target.files?.[0])}
                    data-testid={`storefront-input-${p.id}`}
                  />
                  <div className="flex flex-col gap-1">
                    <Button data-testid={`edit-pharm-${p.id}`} onClick={() => openEdit(p)} size="sm" variant="outline">
                      <PencilSimple size={12} className="mr-1" /> Edit
                    </Button>
                    <Button data-testid={`upload-storefront-${p.id}`} onClick={() => fileRefs.current[p.id]?.click()} size="sm" variant="outline">
                      <Upload size={12} className="mr-1" /> Photo
                    </Button>
                    <Button data-testid={`toggle-status-${p.id}`} onClick={() => toggleStatus(p)} size="sm" variant="outline" className={p.status === 'active' ? 'text-red-600' : 'text-green-600'}>
                      {p.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {pharmacies.length === 0 && (
              <tr><td colSpan="10" className="p-8 text-center text-slate-500">No pharmacies yet — click <b>Add Pharmacy</b> to create one.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Pharmacy' : 'Add Pharmacy'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input data-testid="pharm-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Branch / Outlet</Label><Input data-testid="pharm-branch" value={form.branch_name} onChange={e => setForm({ ...form, branch_name: e.target.value })} /></div>
              <div className="col-span-2"><Label>Area / District *</Label><Input data-testid="pharm-area" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="e.g. Kepong, Kuala Lumpur" /></div>
              <div className="col-span-2"><Label>Full Address *</Label><Input data-testid="pharm-address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Latitude</Label><Input data-testid="pharm-lat" type="number" step="0.0000001" value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })} /></div>
              <div><Label>Longitude</Label><Input data-testid="pharm-lng" type="number" step="0.0000001" value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })} /></div>
              <div className="col-span-2">
                <Button type="button" data-testid="pharm-geocode" variant="outline" size="sm" onClick={geocode}>
                  <MapPin size={14} className="mr-1" /> Geocode from address
                </Button>
              </div>
              <div><Label>Contact Person *</Label><Input data-testid="pharm-contact" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><Label>Phone *</Label><Input data-testid="pharm-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <Label>Commission % Override</Label>
                <Input data-testid="pharm-commission" type="number" step="0.1" value={form.commission_override} onChange={e => setForm({ ...form, commission_override: e.target.value })} placeholder="blank = use global default" />
              </div>
              <div />
              <div><Label>Bank Name</Label><Input data-testid="pharm-bank-name" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} /></div>
              <div><Label>Bank Account No.</Label><Input data-testid="pharm-bank-acc" value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} /></div>
            </div>
            <p className="text-xs text-slate-500">Storefront photo is uploaded from the row action after the pharmacy is saved.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button data-testid="save-pharm-btn" onClick={save} style={{ backgroundColor: '#1E3A8A' }}>{editing ? 'Save Changes' : 'Create Pharmacy'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
